import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import { MealPlannerDragOverlayContent } from "../shared/MealPlannerDragOverlayContent";
import {
  CALORIES_PER_GRAM,
  formatNutritionValue,
} from "../../../../../utils/nutrition-util";
import { calculateNewOptionMealPlannerData } from "../../../../../utils/meal-planner-helper";
import { truncateText } from "../../../../../utils/string-helpers";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import { MealPlannerSimplifiedCalendar } from "./MealPlannerSimplifiedCalendar";
import { createMealDropHandler } from "../utils/meal-drag-drop-helpers";

export const MealPlannerOptionResume = ({
  mealPlan,
  locals,
  tabState,
  planMode,
  api,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { query, nutritionPlans, basicMealsInformation } = locals;
  const { plan = "", athlete, mealPlan: mealPlanId, viewMode } = query;

  // Local state for optimistic updates
  const [localMealPlan, setLocalMealPlan] = useState(mealPlan);

  // Sync local state when prop changes (e.g., after API refresh)
  useEffect(() => {
    setLocalMealPlan(mealPlan);
  }, [mealPlan]);

  const isTemplate =
    (localMealPlan?.type === "template" || !plan) &&
    localMealPlan?.type !== "library";

  const [openedItem, setOpenedItem] = useState(null); // Track the currently opened item
  const selectedNutritionPlan = nutritionPlans
    ? nutritionPlans.find((n) => n._id === plan)
    : null;
  const isFromNutritionPlan = athlete && selectedNutritionPlan;
  const macroInfo = [
    { label: t("mealPlannerOption.proteins"), key: "proteins", className: "protein" },
    { label: t("mealPlannerOption.carbs"), key: "carbs", className: "carbs" },
    { label: t("mealPlannerOption.fats"), key: "fats", className: "fat" },
    { label: t("mealPlannerOption.fibers"), key: "fibers", className: "calories-text" },
    { label: t("mealPlannerOption.calories"), key: "calories", className: "calories-text" },
  ];

  // Get options based on tabState (training or rest)
  const optionsArray = useMemo(() => {
    return tabState === "training"
      ? localMealPlan?.trainingDayOptions || []
      : localMealPlan?.restDayOptions || [];
  }, [localMealPlan, tabState]);

  // Transform options to days format with real data
  const days = useMemo(() => {
    // Get goals from selectedNutritionPlan if available
    const getGoals = () => {
      if (!selectedNutritionPlan) {
        return {
          calories: 0,
          carbs: 0,
          fats: 0,
          proteins: 0,
          fibers: 0,
        };
      }

      const {
        calories = 0,
        carbs = 0,
        fats = 0,
        proteins = 0,
        fibers = 0,
        restDay,
      } = selectedNutritionPlan;
      // For rest days, use restDay goals if available and tabState is "rest"
      if (tabState === "rest" && restDay && typeof restDay === "object") {
        return {
          calories: restDay.calories ?? calories,
          carbs: restDay.carbs ?? carbs,
          fats: restDay.fats ?? fats,
          proteins: restDay.proteins ?? proteins,
          fibers: restDay.fibers ?? fibers,
        };
      }

      // For training days or if restDay doesn't exist, use regular goals
      return {
        calories,
        carbs,
        fats,
        proteins,
        fibers,
      };
    };

    const goals = getGoals();

    return optionsArray.map((option) => {
      // Calculate macrosTotal by summing all meal macrosTotal
      const macrosTotal = (option.meals || []).reduce(
        (acc, meal) => {
          const mealMacros = meal.macrosTotal || {};
          return {
            calories: acc.calories + (mealMacros.calories || 0),
            carbs: acc.carbs + (mealMacros.carbs || 0),
            fats: acc.fats + (mealMacros.fats || 0),
            proteins: acc.proteins + (mealMacros.proteins || 0),
            fibers: acc.fibers + (mealMacros.fibers || 0),
          };
        },
        {
          calories: 0,
          carbs: 0,
          fats: 0,
          proteins: 0,
          fibers: 0,
        },
      );

      // Check if option has any foods
      const hasFoods =
        Array.isArray(option.meals) &&
        option.meals.some(
          (meal) => Array.isArray(meal.foods) && meal.foods.length > 0,
        );

      return {
        date:
          option.title ||
          t("mealPlannerSimplifiedCalendar.optionLabel", {
            number: optionsArray.indexOf(option) + 1,
          }),
        goals,
        macrosTotal,
        hasFoods,
        hasData: hasFoods,
        optionId: option.optionId, // Include optionId for stable reference
      };
    });
  }, [optionsArray, selectedNutritionPlan, tabState, t]);

  /**
   * Function to open the modals
   * @param {*} modal
   * @param {*} optionData - Object with dayType, optionId, date, mealPlanName, planMode, saveAsTemplate
   */
  const handleOpenModal = (modal, optionData) => {
    const {
      dayType,
      optionId,
      date,
      mealPlanName,
      planMode,
      saveAsTemplate,
      hasFoods,
    } = optionData || {};
    const body = {
      "modal[type]": modal,
      ...(date && { date }),
      ...(dayType && { dayType }),
      ...(optionId && { optionId }),
      ...(mealPlanName && { mealPlanName: String(mealPlanName) }),
      ...(planMode && { planMode }),
      ...(saveAsTemplate && { saveAsTemplate }),
      ...(hasFoods !== undefined && { hasFoods }),
    };
    submit(`/actions/refresh/?`, {
      method: "GET",
      body,
    });
  };

  /**
   * Handle modal actions
   */
  const handleModalAction = (action, modal, optionData) => {
    const commonData = {
      dayType: optionData.tabState,
      optionId: optionData.optionId,
      date: optionData.date,
    };

    if (action === "saveAsTemplate") {
      handleOpenModal(modal, {
        ...commonData,
        saveAsTemplate: true,
        planMode: "simplified",
      });
    } else if (action === "replaceFromTemplate" || action === "copyFromTemplate") {
      handleOpenModal(modal, {
        ...commonData,
        planMode: "simplified",
        hasFoods: optionData.hasFoods,
      });
    } else {
      // move, duplicate, rename
      handleOpenModal(modal, {
        ...commonData,
        mealPlanName: optionData.date,
      });
    }
  };

  /**
   * Handle action buttons (Clear, Remove)
   */
  const handleActionButton = (action, optionData) => {
    if (action === "removeAllFoods") {
      removeAllFoods(optionData.event, {
        dayType: optionData.tabState,
        optionId: optionData.optionId,
      });
    } else if (action === "removeOption") {
      handleRemoveOption(optionData.event, {
        dayType: optionData.tabState,
        optionId: optionData.optionId,
      });
    }
  };

  /**
   * Function to remove all the foods of an option
   * @param {*} e
   * @param {*} optionData - Object with dayType and optionId
   */
  const removeAllFoods = (e, optionData) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      process.browser &&
      confirm(t("mealPlannerOption.clearContentConfirm"))
    ) {
      const { dayType, optionId } = optionData;
      const body = {
        planMode: "simplified",
        dayType: dayType, // "training" or "rest"
        optionId: optionId, // Stable ID of the option
        bodyAction: "handle-food",
        handle: "delete",
        isTemplate: isFromNutritionPlan ? false : true,
      };

      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        },
      );
    }
  };

  /**
   * Handle adding a new option to the simplified meal plan
   */
  const handleAddOption = () => {
    const mealPlanIdToUse = mealPlanId || localMealPlan?._id;
    if (!mealPlanIdToUse) {
      console.error("mealPlanId is required");
      return;
    }

    // Calculate meals and title based on the last option of the same type
    const { meals, title } = calculateNewOptionMealPlannerData(
      tabState,
      mealPlan,
    );

    const url = `/actions/meal-planner/handle-simplified-meal-plan-option?mealPlanId=${mealPlanIdToUse}`;
    const body = {
      handle: "add",
      dayType: tabState,
      option: {
        title,
        meals,
      },
    };

    submit(url, {
      method: "POST",
      body,
    });
  };

  /**
   * Handle removing an option from the simplified meal plan
   * @param {*} e
   * @param {*} optionData - Object with dayType and optionId
   */
  const handleRemoveOption = (e, optionData) => {
    e.preventDefault();
    e.stopPropagation();

    const { dayType, optionId } = optionData;
    const mealPlanIdToUse = mealPlanId || localMealPlan?._id;

    if (!mealPlanIdToUse) {
      console.error("mealPlanId is required");
      return;
    }

    if (
      process.browser &&
      confirm(t("mealPlannerOption.removeOptionConfirm"))
    ) {
      const url = `/actions/meal-planner/handle-simplified-meal-plan-option?mealPlanId=${mealPlanIdToUse}`;
      const body = {
        handle: "delete",
        dayType: dayType,
        optionId: optionId,
      };

      submit(url, {
        method: "POST",
        body,
      });
    }
  };

  // State for drag overlay
  const [activeId, setActiveId] = useState(null);
  const [activeData, setActiveData] = useState(null);

  // Create drag and drop handler with optimistic update support
  // Note: isFromNutritionPlan is not used in Simplified mode, but kept for consistency
  const handleDrop = createMealDropHandler(
    api,
    mealPlanId,
    athlete,
    false, // Simplified mode doesn't use isTemplate/isFromNutritionPlan
    {
      // onOptimisticUpdate: Update local state immediately
      onOptimisticUpdate: (updatedMealPlan) => {
        setLocalMealPlan(updatedMealPlan);
      },
      // onRevert: Revert to original state if API fails
      onRevert: (originalMealPlan) => {
        setLocalMealPlan(originalMealPlan);
      },
      // currentMealPlan: Pass current state for optimistic update
      currentMealPlan: localMealPlan,
      type: "move", // Default operation type
      flash: locals.flash,
      submit: submit,
    },
  );

  // Handle drag start for overlay - for individual foods and customMeals (not full meals)
  const handleDragStart = (event) => {
    // DragDropProvider passes operation.source in the event
    const source = event.operation?.source;
    if (!source) return;

    // Try different ways to access data
    const data = source.data?.current || source.data;
    // Track for individual foods (data.type === "food" or "verified-food") and customMeals (data.type === "customMeal")
    // Meals completos NO tienen "type" en su data, solo tienen planMode y mealType
    if (data && (data.type === "food" || data.type === "verified-food" || data.type === "customMeal")) {
      setActiveId(source.id);
      setActiveData(data);
    } else {
      // Clear overlay for full meals (they work fine without overlay)
      setActiveId(null);
      setActiveData(null);
    }
  };

  // Handle drag end - call drop handler and clear overlay
  const handleDragEnd = (event) => {
    // Clear overlay first (so it disappears immediately)
    setActiveId(null);
    setActiveData(null);

    // Execute drop handler immediately - no delay needed with DragOverlay
    handleDrop(event);
  };

  // Handle drag cancel
  const handleDragCancel = () => {
    setActiveId(null);
    setActiveData(null);
  };

  return (
    <div className="resume simplified">
      <div className="resume-nutrition">
        {days.map(
          (
            {
              date,
              goals,
              macrosTotal,
              hasFoods = false,
              hasData = false,
              optionId,
            },
            index,
          ) => {
            const isOpened = openedItem === index; // Check if the current item is opened
            return (
              <div
                key={`${optionId || date}-${index}`}
                className="resume-container"
              >
                {/* header*/}
                <div className="resume-stats">
                  <div
                    className={
                      hasFoods
                        ? `header-container bg-(--dark-grey)`
                        : `header-container bg-(--footer-background-color)`
                    }
                  >
                    <div className="header-text">
                      <span>{truncateText(date, 52)}</span>
                    </div>
                    {!viewMode && (
                      <div className="actions">
                        {!(isTemplate && planMode === "simplified") && (
                          <ToolTip
                            id={`copy-from-template-${optionId || date}-${index}`}
                            body={t("mealPlannerOption.importSingleDayTemplate")}
                            float={false}
                          >
                            <a
                              className="copy copy--labeled"
                              onClick={() =>
                                handleModalAction(
                                  hasFoods
                                    ? "replaceFromTemplate"
                                    : "copyFromTemplate",
                                  "replace-option-from-template",
                                  { tabState, optionId, date, hasFoods },
                                )
                              }
                            >
                              <SvgIcon src="/img/new-icons/import-in.svg" />
                              <span className="copy-label">
                                {t("mealPlannerOption.import")}
                              </span>
                            </a>
                          </ToolTip>
                        )}
                        <details
                          role="list"
                          open={isOpened}
                          className="enabled"
                        >
                          <summary
                            onClick={(e) => {
                              e.preventDefault();
                              setOpenedItem(isOpened ? null : index);
                            }}
                            onBlur={(e) => {
                              e.preventDefault();
                              setOpenedItem(null);
                            }}
                            aria-haspopup="listbox"
                            className="p-0! h-auto!"
                          >
                            <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
                          </summary>
                          <ul
                            role="listbox"
                            className={isOpened ? "opened" : ""}
                          >
                            {[
                              {
                                action: "move",
                                label: t("mealPlannerOption.move"),
                                modal: "move-meal-plan",
                                type: "modal",
                              },
                              {
                                action: "duplicate",
                                label: t("mealPlannerOption.duplicate"),
                                modal: "duplicate-meal-plan",
                                type: "modal",
                              },
                              {
                                action: "saveAsTemplate",
                                label: t("mealPlannerOption.saveAsTemplate"),
                                modal: "handle-meal-plan",
                                type: "modal",
                              },
                              {
                                action: "rename",
                                label: t("mealPlannerOption.rename"),
                                modal: "handle-meal-plan-option-name",
                                type: "modal",
                              },
                              {
                                action: "clear",
                                label: t("mealPlannerOption.clear"),
                                buttonAction: "removeAllFoods",
                                type: "action",
                                danger: true,
                              },
                              {
                                action: "remove",
                                label: t("mealPlannerOption.remove"),
                                buttonAction: "removeOption",
                                type: "action",
                                danger: true,
                                disabled: localMealPlan?.published === true,
                              },
                            ]
                              .filter((item) => {
                                // For simplified meal plan templates, only show Clear and Rename
                                if (isTemplate && planMode === "simplified") {
                                  return (
                                    item.action === "clear" ||
                                    item.action === "rename"
                                  );
                                }
                                // If no foods, show Rename and Remove
                                if (!hasFoods) {
                                  return (
                                    item.action === "rename" ||
                                    item.action === "remove"
                                  );
                                }
                                // If has foods, show all options
                                return true;
                              })
                              .map((item, itemIndex) => {
                                const isDisabled = item.disabled === true;
                                const tooltipId = `remove-option-tooltip-${index}-${itemIndex}`;
                                const listItem = (
                                  <li
                                    style={
                                      isDisabled
                                        ? {
                                            cursor: "not-allowed",
                                          }
                                        : {}
                                    }
                                  >
                                    <a
                                      className={
                                        item.danger ? "text-(--danger)!" : ""
                                      }
                                      style={
                                        isDisabled
                                          ? {
                                              opacity: 0.5,
                                              pointerEvents: "none",
                                            }
                                          : {}
                                      }
                                      onMouseDown={(e) => {
                                        if (isDisabled) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          return;
                                        }
                                        if (item.type === "modal") {
                                          handleModalAction(
                                            item.action,
                                            item.modal,
                                            {
                                              tabState,
                                              optionId,
                                              date,
                                            },
                                          );
                                        } else if (item.type === "action") {
                                          handleActionButton(item.buttonAction, {
                                            event: e,
                                            tabState,
                                            optionId,
                                          });
                                        }
                                      }}
                                    >
                                      {item.label}
                                    </a>
                                  </li>
                                );

                                return isDisabled && item.action === "remove" ? (
                                  <ToolTip
                                    key={itemIndex}
                                    id={tooltipId}
                                    className="help-tooltip"
                                    body={t("mealPlannerOption.unpublishToRemove")}
                                    multiline
                                    place="bottom"
                                    hintClassNames="remove-option-tooltip-high-z"
                                  >
                                    {listItem}
                                  </ToolTip>
                                ) : (
                                  <React.Fragment key={itemIndex}>
                                    {listItem}
                                  </React.Fragment>
                                );
                              })}
                          </ul>
                        </details>
                      </div>
                    )}
                  </div>
                  {/* content */}
                  <div
                    className={`content-container ${
                      hasData
                        ? "bg-(--dark-grey)"
                        : "bg-(--footer-background-color)"
                    } ${!isFromNutritionPlan ? "h-44.25!" : ""}`}
                  >
                    {macroInfo.map(({ label, key, className }) => {
                      const actualToUse =
                        macrosTotal?.[key] ??
                        macrosTotal?.macrosTotal?.[key] ??
                        0;

                      const actual = formatNutritionValue(actualToUse);
                      // Get the calories per gram
                      const caloriesPerGram = CALORIES_PER_GRAM[key] || 0;
                      // Calories generated by that macro
                      const macroCalories = actualToUse * caloriesPerGram;
                      const totalCalories = macrosTotal?.calories || 0;
                      const percentage =
                        key !== "calories" &&
                        caloriesPerGram > 0 &&
                        totalCalories > 0
                          ? ((macroCalories / totalCalories) * 100).toFixed(0)
                          : "0";
                      const goal = formatNutritionValue(goals[key] || 0);
                      const diff = (macrosTotal[key] || 0) - (goals[key] || 0);
                      const absDiff = formatNutritionValue(Math.abs(diff));

                      return (
                        <div className="info" key={label}>
                          <div className="flex flex-col gap-0.5">
                            <span className={className}>{label}</span>
                            {isFromNutritionPlan && (
                              <span className="opacity-50">
                                ({diff < 0
                                  ? t("mealPlannerOption.under")
                                  : t("mealPlannerOption.over")})
                              </span>
                            )}
                          </div>
                          <div className="goals">
                            <span>
                              {actual}
                              {isFromNutritionPlan && `/${goal}`}
                              {key === "calories" ? " kcal" : "g"}
                              {!isFromNutritionPlan && percentage > 0 && (
                                <span className="text-[0.75rem] opacity-70">
                                  {" "}
                                  ({percentage}%)
                                </span>
                              )}
                            </span>
                            {isFromNutritionPlan && (
                              <span className="opacity-50">
                                ({absDiff} {diff < 0
                                  ? t("mealPlannerOption.left")
                                  : t("mealPlannerOption.excess")})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          },
        )}
        {!viewMode && !(isTemplate && planMode === "simplified") && (
          <div
            className={`add-option-container ${
              isFromNutritionPlan ? "is-from-nutrition-plan" : ""
            }`}
          >
            <button
              type="button"
              onClick={handleAddOption}
              className="add-option-link"
            >
              <SvgIcon src="/img/new-icons/add-square-outline.svg" />
              <span>{t("mealPlannerOption.addOption")}</span>
            </button>
          </div>
        )}
      </div>
      {/* calendar */}
      {/*(SIMPLIFIED)drag is go and drop is receive */}
      <DragDropProvider
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* DragOverlay - render when dragging food or customMeal */}
        {activeId &&
          activeData &&
          (activeData.type === "food" || activeData.type === "verified-food" || activeData.type === "customMeal") && (
            <DragOverlay dropAnimation={null} style={{ pointerEvents: "none" }}>
              <MealPlannerDragOverlayContent
                activeId={activeId}
                activeData={activeData}
              />
            </DragOverlay>
          )}
        <MealPlannerSimplifiedCalendar
          mealPlan={localMealPlan}
          tabState={tabState}
          viewMode={viewMode}
          isFromNutritionPlan={isFromNutritionPlan}
          query={query}
          basicMealsInformation={basicMealsInformation}
        />
      </DragDropProvider>
    </div>
  );
};

MealPlannerOptionResume.propTypes = {
  mealPlan: PropTypes.object,
  locals: PropTypes.object,
  tabState: PropTypes.string,
  planMode: PropTypes.string,
  api: PropTypes.func,
};
